import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Helmet } from "react-helmet-async";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Copy, Download, ExternalLink, Loader2, RefreshCw, Undo2, X } from "lucide-react";

import { toast } from "sonner";

const stripeSessionUrl = (sessionId: string | null, environment?: string | null) => {
  if (!sessionId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}payments?query=${encodeURIComponent(sessionId)}`;
};

const stripePaymentIntentUrl = (piId: string | null, environment?: string | null) => {
  if (!piId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}payments/${encodeURIComponent(piId)}`;
};

const stripeCustomerUrl = (customerId: string | null, environment?: string | null) => {
  if (!customerId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}customers/${encodeURIComponent(customerId)}`;
};

const StripeCustomerLink = ({
  customerId,
  environment,
}: {
  customerId: string | null;
  environment?: string | null;
}) => {
  if (!customerId) return null;
  const url = stripeCustomerUrl(customerId, environment)!;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline"
      title={`Open customer ${customerId} in Stripe dashboard`}
    >
      {customerId}
      <ExternalLink className="h-2.5 w-2.5 shrink-0" />
    </a>
  );
};


const PaymentIntentCell = ({
  paymentIntentId,
  environment,
}: {
  paymentIntentId: string | null;
  environment?: string | null;
}) => {
  const [copied, setCopied] = useState(false);
  if (!paymentIntentId) return <span className="text-muted-foreground">—</span>;
  const url = stripePaymentIntentUrl(paymentIntentId, environment)!;
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(paymentIntentId);
      setCopied(true);
      toast.success("Payment intent ID copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };
  return (
    <div className="inline-flex items-start gap-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs break-all text-primary hover:underline"
        title="Open PaymentIntent in Stripe dashboard"
      >
        {paymentIntentId}
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Copy payment intent ID"
        aria-label="Copy payment intent ID"
      >
        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
};

const StripeSessionCell = ({
  sessionId,
  environment,
}: {
  sessionId: string | null;
  environment?: string | null;
}) => {
  const [copied, setCopied] = useState(false);
  if (!sessionId) return <span>—</span>;
  const url = stripeSessionUrl(sessionId, environment);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopied(true);
      toast.success("Session ID copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="inline-flex items-start gap-1">
      <a
        href={url!}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs break-all text-primary hover:underline"
        title="Open in Stripe dashboard"
      >
        {sessionId}
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Copy session ID"
        aria-label="Copy session ID"
      >
        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
};

const RefundAction = ({
  paymentIntentId,
  environment,
  status,
  amountLabel,
  onDone,
}: {
  paymentIntentId: string | null;
  environment: string;
  status: string;
  amountLabel: string;
  onDone: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState<string>("none");

  if (!paymentIntentId) return <span className="text-muted-foreground text-xs">—</span>;

  const isRefundable = ["succeeded", "paid", "complete", "completed", "requires_capture"].includes(
    status?.toLowerCase(),
  );
  const label = isRefundable ? "Refund" : "Cancel";

  const submit = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-refund-payment", {
      body: {
        payment_intent_id: paymentIntentId,
        environment,
        reason: reason === "none" ? undefined : reason,
      },
    });
    setBusy(false);
    if (error || (data as { error?: string })?.error) {
      const msg = (data as { error?: string })?.error ?? error?.message ?? "Failed";
      toast.error(msg);
      return;
    }
    const action = (data as { action?: string })?.action;
    toast.success(action === "refund" ? "Refund issued" : "Payment canceled");
    setOpen(false);
    onDone();
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
          <Undo2 className="mr-1 h-3 w-3" />
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isRefundable ? "Refund this payment?" : "Cancel this payment?"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {isRefundable
                  ? `This will issue a full refund of ${amountLabel} via Stripe. This cannot be undone.`
                  : `This will cancel the PaymentIntent in Stripe. No charge will be made.`}
              </p>
              <div className="rounded bg-muted p-2 font-mono text-xs break-all">
                {paymentIntentId} · {environment}
              </div>
              {isRefundable && (
                <div>
                  <Label className="text-xs">Reason (optional)</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                      <SelectItem value="duplicate">Duplicate</SelectItem>
                      <SelectItem value="fraudulent">Fraudulent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Back</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {busy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
            {isRefundable ? "Refund now" : "Cancel payment"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};



type Payment = {
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_customer_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  amount_total: number | null;
  currency: string | null;
  status: string;
  mode: string | null;
  environment: string;
  created_at: string;
  updated_at: string;
};

type EventLog = {
  created_at: string;
  source: string;
  event_type: string;
  severity: string;
  environment: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  customer_email: string | null;
  message: string | null;
};

const ZERO_DECIMAL = new Set(["bif","clp","djf","gnf","jpy","kmf","krw","mga","pyg","rwf","ugx","vnd","vuv","xaf","xof","xpf"]);
const THREE_DECIMAL = new Set(["bhd","jod","kwd","omr","tnd"]);
const toMajor = (a: number, c: string) => {
  const cc = c.toLowerCase();
  if (ZERO_DECIMAL.has(cc)) return a;
  if (THREE_DECIMAL.has(cc)) return a / 1000;
  return a / 100;
};
const formatMoney = (a: number | null, c: string | null) => {
  if (a == null) return { amount: "—", currency: "" };
  const cur = (c ?? "usd").toUpperCase();
  try {
    return {
      amount: new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(toMajor(a, cur)),
      currency: cur,
    };
  } catch {
    return { amount: `${toMajor(a, cur).toFixed(2)}`, currency: cur };
  }
};
const formatAmount = (a: number | null, c: string | null) => {
  const { amount, currency } = formatMoney(a, c);
  return amount === "—" ? "—" : `${amount} ${currency && !amount.includes(currency) ? currency : ""}`.trim();
};

const fmtDate = (s: string) => new Date(s).toLocaleString();
const fmtRelative = (s: string) => {
  const diff = Date.now() - new Date(s).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

export default function AdminCheckouts() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [events, setEvents] = useState<EventLog[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.functions.invoke("admin-list-failed-checkouts");
    if (error) setError(error.message);
    else {
      setPayments(data?.payments ?? []);
      setEvents(data?.events ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const emailQuery = searchParams.get("email") ?? "";
  const idQuery = searchParams.get("id") ?? "";
  const statusFilter = searchParams.get("status") ?? "all";
  const startDate = searchParams.get("from") ?? "";
  const endDate = searchParams.get("to") ?? "";

  const updateParam = (key: string, value: string, defaultValue = "") => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };
  const setEmailQuery = (v: string) => updateParam("email", v);
  const setIdQuery = (v: string) => updateParam("id", v);
  const setStatusFilter = (v: string) => updateParam("status", v, "all");
  const setStartDate = (v: string) => updateParam("from", v);
  const setEndDate = (v: string) => updateParam("to", v);


  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    payments.forEach((p) => p.status && set.add(p.status));
    return Array.from(set).sort();
  }, [payments]);

  const inRange = (iso: string) => {
    const t = new Date(iso).getTime();
    if (startDate) {
      const s = new Date(startDate).getTime();
      if (t < s) return false;
    }
    if (endDate) {
      // include the end date entirely
      const e = new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1;
      if (t > e) return false;
    }
    return true;
  };

  const emailMatches = (email: string | null) => {
    if (!emailQuery.trim()) return true;
    return (email ?? "").toLowerCase().includes(emailQuery.trim().toLowerCase());
  };

  const idMatches = (
    sessionId: string | null,
    paymentIntentId?: string | null,
  ) => {
    const q = idQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (sessionId ?? "").toLowerCase().includes(q) ||
      (paymentIntentId ?? "").toLowerCase().includes(q)
    );
  };

  const filteredPayments = useMemo(
    () =>
      payments.filter(
        (p) =>
          emailMatches(p.customer_email) &&
          idMatches(p.stripe_session_id, p.stripe_payment_intent_id) &&
          (statusFilter === "all" || p.status === statusFilter) &&
          inRange(p.created_at),
      ),
    [payments, emailQuery, idQuery, statusFilter, startDate, endDate],
  );

  const filteredEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          emailMatches(e.customer_email) &&
          idMatches(e.stripe_session_id, null) &&
          inRange(e.created_at),
      ),
    [events, emailQuery, idQuery, startDate, endDate],
  );

  // ---- Sorting ----
  type PaymentSortKey =
    | "created"
    | "status"
    | "customer"
    | "amount"
    | "currency"
    | "session"
    | "payment_intent";

  type EventSortKey = "created" | "customer" | "session";
  type SortDir = "asc" | "desc";

  const paymentSort = (searchParams.get("psort") ?? "created") as PaymentSortKey;
  const paymentDir = (searchParams.get("pdir") ?? "desc") as SortDir;
  const eventSort = (searchParams.get("esort") ?? "created") as EventSortKey;
  const eventDir = (searchParams.get("edir") ?? "desc") as SortDir;

  const toggleSort = (
    keyParam: "psort" | "esort",
    dirParam: "pdir" | "edir",
    key: string,
    defaultKey: string,
  ) => {
    const currentKey = searchParams.get(keyParam) ?? defaultKey;
    const currentDir = (searchParams.get(dirParam) ?? "desc") as SortDir;
    const nextDir: SortDir = currentKey === key && currentDir === "asc" ? "desc" : "asc";
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === defaultKey && nextDir === "desc") {
          next.delete(keyParam);
          next.delete(dirParam);
        } else {
          next.set(keyParam, key);
          next.set(dirParam, nextDir);
        }
        return next;
      },
      { replace: true },
    );
  };

  const cmp = (a: unknown, b: unknown, dir: SortDir) => {
    const aNil = a == null || a === "";
    const bNil = b == null || b === "";
    if (aNil && bNil) return 0;
    if (aNil) return 1; // nulls always last
    if (bNil) return -1;
    let r: number;
    if (typeof a === "number" && typeof b === "number") r = a - b;
    else r = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
    return dir === "asc" ? r : -r;
  };

  const sortedPayments = useMemo(() => {
    const arr = [...filteredPayments];
    arr.sort((a, b) => {
      switch (paymentSort) {
        case "status":
          return cmp(a.status, b.status, paymentDir);
        case "customer":
          return cmp(a.customer_email, b.customer_email, paymentDir);
        case "amount":
          return cmp(a.amount_total, b.amount_total, paymentDir);
        case "currency":
          return cmp(a.currency, b.currency, paymentDir);
        case "session":
          return cmp(a.stripe_session_id, b.stripe_session_id, paymentDir);
        case "payment_intent":
          return cmp(a.stripe_payment_intent_id, b.stripe_payment_intent_id, paymentDir);
        case "created":
        default:
          return cmp(new Date(a.created_at).getTime(), new Date(b.created_at).getTime(), paymentDir);
      }

    });
    return arr;
  }, [filteredPayments, paymentSort, paymentDir]);

  const sortedEvents = useMemo(() => {
    const arr = [...filteredEvents];
    arr.sort((a, b) => {
      switch (eventSort) {
        case "customer":
          return cmp(a.customer_email, b.customer_email, eventDir);
        case "session":
          return cmp(a.stripe_session_id, b.stripe_session_id, eventDir);
        case "created":
        default:
          return cmp(new Date(a.created_at).getTime(), new Date(b.created_at).getTime(), eventDir);
      }
    });
    return arr;
  }, [filteredEvents, eventSort, eventDir]);

  const SortHeader = ({
    label,
    active,
    dir,
    onClick,
    className,
  }: {
    label: string;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
        aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        {active ? (
          dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-50" />
        )}
      </button>
    </TableHead>
  );

  // ---- Pagination ----
  const PAGE_SIZES = [25, 50, 100, 200];
  const parseSize = (v: string | null) => {
    const n = Number(v);
    return PAGE_SIZES.includes(n) ? n : 50;
  };
  const parsePage = (v: string | null) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
  };
  const paymentPageSize = parseSize(searchParams.get("psize"));
  const paymentPage = parsePage(searchParams.get("ppage"));
  const eventPageSize = parseSize(searchParams.get("esize"));
  const eventPage = parsePage(searchParams.get("epage"));

  const paymentPageCount = Math.max(1, Math.ceil(sortedPayments.length / paymentPageSize));
  const eventPageCount = Math.max(1, Math.ceil(sortedEvents.length / eventPageSize));
  const clampedPaymentPage = Math.min(paymentPage, paymentPageCount);
  const clampedEventPage = Math.min(eventPage, eventPageCount);

  const pagedPayments = useMemo(
    () =>
      sortedPayments.slice(
        (clampedPaymentPage - 1) * paymentPageSize,
        clampedPaymentPage * paymentPageSize,
      ),
    [sortedPayments, clampedPaymentPage, paymentPageSize],
  );
  const pagedEvents = useMemo(
    () =>
      sortedEvents.slice(
        (clampedEventPage - 1) * eventPageSize,
        clampedEventPage * eventPageSize,
      ),
    [sortedEvents, clampedEventPage, eventPageSize],
  );

  const setPage = (param: "ppage" | "epage", page: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete(param);
        else next.set(param, String(page));
        return next;
      },
      { replace: true },
    );
  };
  const setPageSize = (
    sizeParam: "psize" | "esize",
    pageParam: "ppage" | "epage",
    size: number,
  ) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (size === 50) next.delete(sizeParam);
        else next.set(sizeParam, String(size));
        next.delete(pageParam); // reset to page 1
        return next;
      },
      { replace: true },
    );
  };

  const Pagination = ({
    total,
    page,
    pageCount,
    pageSize,
    onPageChange,
    onPageSizeChange,
    label,
  }: {
    total: number;
    page: number;
    pageCount: number;
    pageSize: number;
    onPageChange: (p: number) => void;
    onPageSizeChange: (s: number) => void;
    label: string;
  }) => {
    if (total === 0) return null;
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return (
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-muted-foreground">
        <div>
          Showing <span className="font-medium text-foreground">{first}–{last}</span> of{" "}
          <span className="font-medium text-foreground">{total}</span> {label}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(1)}
              disabled={page <= 1}
              aria-label="First page"
            >«</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >‹</Button>
            <span className="px-2 tabular-nums">
              Page {page} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pageCount}
              aria-label="Next page"
            >›</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(pageCount)}
              disabled={page >= pageCount}
              aria-label="Last page"
            >»</Button>
          </div>
        </div>
      </div>
    );
  };




  const anyFilterActive =
    emailQuery.trim() !== "" ||
    idQuery.trim() !== "" ||
    statusFilter !== "all" ||
    startDate !== "" ||
    endDate !== "";
  const clearFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["email", "id", "status", "from", "to"].forEach((k) => next.delete(k));
        return next;
      },
      { replace: true },
    );
  };


  const exportCsv = () => {
    if (filteredPayments.length === 0) {
      toast.error("No payments to export");
      return;
    }
    const headers = [
      "created_at",
      "status",
      "environment",
      "mode",
      "customer_email",
      "customer_name",
      "amount_total",
      "currency",
      "stripe_session_id",
      "stripe_payment_intent_id",
      "stripe_customer_id",
    ];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = sortedPayments.map((p) => [
      p.created_at,
      p.status,
      p.environment,
      p.mode ?? "",
      p.customer_email ?? "",
      p.customer_name ?? "",
      p.amount_total ?? "",
      p.currency ?? "",
      p.stripe_session_id ?? "",
      p.stripe_payment_intent_id ?? "",
      p.stripe_customer_id ?? "",
    ]);
    const csv =
      [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n") + "\r\n";
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `checkouts-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredPayments.length} payments`);
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <Helmet>
        <title>Admin — Failed & Incomplete Checkouts</title>
        <meta name="description" content="Internal admin view of recent failed or incomplete Stripe checkouts." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Failed & Incomplete Checkouts</h1>
          <p className="text-muted-foreground">Recent non-completed Stripe sessions and payment errors.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportCsv}
            variant="outline"
            size="sm"
            disabled={loading || filteredPayments.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV{filteredPayments.length > 0 ? ` (${filteredPayments.length})` : ""}
          </Button>
          <Button onClick={load} variant="outline" size="sm" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>


      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6 text-destructive">{error}</CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <Label htmlFor="email-filter" className="text-xs">Customer email</Label>
              <Input
                id="email-filter"
                placeholder="Search email…"
                value={emailQuery}
                onChange={(e) => setEmailQuery(e.target.value)}
              />
            </div>
            <div className="lg:col-span-2">
              <Label htmlFor="id-filter" className="text-xs">Session or payment intent ID</Label>
              <Input
                id="id-filter"
                placeholder="cs_… or pi_…"
                value={idQuery}
                onChange={(e) => setIdQuery(e.target.value)}
                className="font-mono"
              />
            </div>

            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="start-date" className="text-xs">From</Label>
              <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="end-date" className="text-xs">To</Label>
              <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          {anyFilterActive && (
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Showing {filteredPayments.length} of {payments.length} payments · {filteredEvents.length} of {events.length} events
              </span>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-3 w-3" /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Incomplete payments ({filteredPayments.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader
                  label="Created"
                  active={paymentSort === "created"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "created", "created")}
                />
                <SortHeader
                  label="Status"
                  active={paymentSort === "status"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "status", "created")}
                />
                <SortHeader
                  label="Customer email"
                  active={paymentSort === "customer"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "customer", "created")}
                />
                <SortHeader
                  label="Amount"
                  active={paymentSort === "amount"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "amount", "created")}
                />
                <SortHeader
                  label="Currency"
                  active={paymentSort === "currency"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "currency", "created")}
                />
                <TableHead>Mode</TableHead>
                <TableHead>Env</TableHead>
                <SortHeader
                  label="Session ID"
                  active={paymentSort === "session"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "session", "created")}
                />
                <SortHeader
                  label="Payment intent"
                  active={paymentSort === "payment_intent"}
                  dir={paymentDir}
                  onClick={() => toggleSort("psort", "pdir", "payment_intent", "created")}
                />
                <TableHead>Actions</TableHead>

              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPayments.length === 0 && !loading && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">No incomplete checkouts.</TableCell></TableRow>
              )}

              {pagedPayments.map((p) => {


                const money = formatMoney(p.amount_total, p.currency);
                return (
                  <TableRow key={p.stripe_session_id ?? `${p.created_at}-${p.customer_email}`}>
                    <TableCell className="whitespace-nowrap text-xs">
                      <div>{fmtDate(p.created_at)}</div>
                      <div className="text-muted-foreground">{fmtRelative(p.created_at)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          p.status === "complete" || p.status === "paid"
                            ? "default"
                            : p.status === "failed"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{p.customer_email ?? "—"}</div>
                      {p.customer_name && <div className="text-muted-foreground text-xs">{p.customer_name}</div>}
                      <StripeCustomerLink customerId={p.stripe_customer_id} environment={p.environment} />
                    </TableCell>

                    <TableCell className="text-sm font-medium tabular-nums whitespace-nowrap">{money.amount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{money.currency || "—"}</TableCell>
                    <TableCell className="text-xs">{p.mode ?? "—"}</TableCell>
                    <TableCell><Badge variant="outline">{p.environment}</Badge></TableCell>
                    <TableCell><StripeSessionCell sessionId={p.stripe_session_id} environment={p.environment} /></TableCell>
                    <TableCell><PaymentIntentCell paymentIntentId={p.stripe_payment_intent_id} environment={p.environment} /></TableCell>
                    <TableCell>
                      <RefundAction
                        paymentIntentId={p.stripe_payment_intent_id}
                        environment={p.environment}
                        status={p.status}
                        amountLabel={`${money.amount}${money.currency ? ` ${money.currency}` : ""}`}
                        onDone={load}
                      />
                    </TableCell>
                  </TableRow>

                );
              })}
            </TableBody>
          </Table>
          <Pagination
            total={sortedPayments.length}
            page={clampedPaymentPage}
            pageCount={paymentPageCount}
            pageSize={paymentPageSize}
            onPageChange={(p) => setPage("ppage", p)}
            onPageSizeChange={(s) => setPageSize("psize", "ppage", s)}
            label="payments"
          />
        </CardContent>
      </Card>


      <Card>
        <CardHeader>
          <CardTitle>Recent payment errors & warnings ({filteredEvents.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader
                  label="Time"
                  active={eventSort === "created"}
                  dir={eventDir}
                  onClick={() => toggleSort("esort", "edir", "created", "created")}
                />
                <TableHead>Severity</TableHead>
                <TableHead>Event</TableHead>
                <SortHeader
                  label="Customer"
                  active={eventSort === "customer"}
                  dir={eventDir}
                  onClick={() => toggleSort("esort", "edir", "customer", "created")}
                />
                <SortHeader
                  label="Session"
                  active={eventSort === "session"}
                  dir={eventDir}
                  onClick={() => toggleSort("esort", "edir", "session", "created")}
                />
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEvents.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No error events.</TableCell></TableRow>
              )}
              {pagedEvents.map((e, i) => (

                <TableRow key={i}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(e.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant={e.severity === "error" ? "destructive" : "secondary"}>{e.severity}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{e.event_type}</div>
                    <div className="text-muted-foreground">{e.source}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{e.customer_email ?? "—"}</div>
                    <StripeCustomerLink customerId={e.stripe_customer_id} environment={e.environment} />
                  </TableCell>

                  <TableCell className="max-w-[180px]"><StripeSessionCell sessionId={e.stripe_session_id} environment={e.environment} /></TableCell>
                  <TableCell className="text-xs max-w-[280px]">{e.message ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            total={sortedEvents.length}
            page={clampedEventPage}
            pageCount={eventPageCount}
            pageSize={eventPageSize}
            onPageChange={(p) => setPage("epage", p)}
            onPageSizeChange={(s) => setPageSize("esize", "epage", s)}
            label="events"
          />
        </CardContent>
      </Card>

    </div>
  );
}
